// src/registered/site-admin/report_management/blueprint/meterSimulationService.js
//
// Simulates the arrival of readings from registered smart / utility meters.
//
// The real ingestion entry point (DCC/MDR API, CSV drop, MOP portal…) is still
// unspecified, so this stands in for it: it generates reading batches in the
// same source-agnostic shape `ESG.usp_MeterReading_IngestBatch` expects — an
// array of { scheme, identifier, readingKind, unit, timestamp, value } rows —
// and rolls them up to period totals that pre-fill the Conduct ESG Reporting
// form (Energy and Water topics).
//
// Storage: localStorage (always) + best-effort S3 mirror under
//   meter-sim/<companyId>/<batchId>.json  in the elements bucket.

import { GenericFileUploader } from "../../../upload/services/genericFileUploader";

const ELEMENTS_BUCKET =
  process.env.REACT_APP_SITE_TEMPLATE_ELEMENTS_BUCKET ||
  "esg-template-elements-management";

const LOCAL_KEY = "sustina.simulatedMeterBatches.v1";

const nowIso = () => new Date().toISOString();
const newId = (p = "batch") =>
  `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// simulated meter points — what a registered site is assumed to have
// ---------------------------------------------------------------------------
//
// `fieldPath` maps the rolled-up total onto a field in blueprintReportingTemplates.
// REGISTER readings are cumulative (period usage = last − first); INTERVAL
// readings are per-period deltas (period usage = Σ).

export const SIMULATED_METER_POINTS = [
  {
    meterId: "ELEC-IMP-001",
    label: "Main incomer — active energy import",
    commodity: "ELECTRICITY",
    scheme: "OBIS",
    identifier: "1-0:1.8.0",
    readingKind: "REGISTER",
    unit: "kWh",
    fieldPath: "energy.electricity_total_kwh",
    baseDailyUsage: 3200, // kWh/day — a mid-size production site
    weekendFactor: 0.45,
    jitter: 0.08,
  },
  {
    meterId: "ELEC-GEN-PV1",
    label: "Rooftop PV — active energy export",
    commodity: "ELECTRICITY",
    scheme: "OBIS",
    identifier: "1-0:2.8.0",
    readingKind: "REGISTER",
    unit: "kWh",
    fieldPath: "energy.pv_exported_kwh",
    baseDailyUsage: 180,
    weekendFactor: 1.0,
    jitter: 0.25,
  },
  {
    meterId: "GAS-001",
    label: "Boiler house — natural gas",
    commodity: "GAS",
    scheme: "OBIS",
    identifier: "7-1:3.0.0",
    readingKind: "REGISTER",
    unit: "kWh",
    fieldPath: "energy.natural_gas_kwh",
    baseDailyUsage: 1500,
    weekendFactor: 0.6,
    jitter: 0.12,
  },
  {
    meterId: "HEAT-DH-001",
    label: "District heat interface",
    commodity: "HEAT",
    scheme: "OBIS",
    identifier: "6-0:1.0.0",
    readingKind: "REGISTER",
    unit: "kWh",
    fieldPath: "energy.district_heat_kwh",
    baseDailyUsage: 0, // most sites have none; leave zeroed
    weekendFactor: 0.5,
    jitter: 0.1,
  },
  {
    meterId: "WATER-001",
    label: "Site water — main meter",
    commodity: "WATER",
    scheme: "OBIS",
    identifier: "8-0:1.0.0",
    readingKind: "REGISTER",
    unit: "L",
    fieldPath: "water.total_withdrawn_l",
    baseDailyUsage: 42000, // litres/day
    weekendFactor: 0.35,
    jitter: 0.1,
  },
  {
    meterId: "WATER-PROC-002",
    label: "Process water sub-meter",
    commodity: "WATER",
    scheme: "OBIS",
    identifier: "8-1:1.0.0",
    readingKind: "REGISTER",
    unit: "L",
    fieldPath: "water.process_water_l",
    baseDailyUsage: 28000,
    weekendFactor: 0.3,
    jitter: 0.12,
  },
];

// ---------------------------------------------------------------------------
// local store
// ---------------------------------------------------------------------------

function readLocal() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && parsed.byCompany
      ? parsed
      : { byCompany: {} };
  } catch {
    return { byCompany: {} };
  }
}

function writeLocal(store) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota */
  }
}

// ---------------------------------------------------------------------------
// generation
// ---------------------------------------------------------------------------

function daysBetween(startIso, endIso) {
  const a = new Date(startIso);
  const b = new Date(endIso);
  const ms = b.getTime() - a.getTime();
  return Math.max(1, Math.round(ms / 86400000));
}

function isWeekend(date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

/** deterministic-ish noise so repeated runs of the same period look stable */
function seededJitter(seedStr, amplitude) {
  let h = 0;
  for (let i = 0; i < seedStr.length; i += 1) {
    h = (h * 31 + seedStr.charCodeAt(i)) & 0xffffffff;
  }
  const unit = ((h >>> 0) % 1000) / 1000; // 0..1
  return 1 + (unit - 0.5) * 2 * amplitude; // 1 ± amplitude
}

/**
 * Build one batch of daily register readings for every simulated meter point.
 * @param {{companyId, companyName, periodStart, periodEnd}} opts  ISO dates
 * @returns {Object} batch  (also persisted locally + best-effort to S3)
 */
export async function generateReadingBatch({
  companyId,
  companyName = "",
  periodStart,
  periodEnd,
}) {
  const cid = companyId != null ? String(companyId) : "unassigned";
  const days = daysBetween(periodStart, periodEnd);
  const start = new Date(periodStart);
  const batchId = newId();

  const readings = [];

  SIMULATED_METER_POINTS.forEach((mp) => {
    // random-ish opening register value, stable per meter+company
    const opening = Math.round(
      100000 * seededJitter(`${cid}:${mp.meterId}:open`, 0.9) + 50000,
    );
    let register = opening;

    // opening read at 00:00 on day 0
    readings.push({
      meterId: mp.meterId,
      meterLabel: mp.label,
      commodity: mp.commodity,
      scheme: mp.scheme,
      identifier: mp.identifier,
      readingKind: mp.readingKind,
      unit: mp.unit,
      timestamp: new Date(start.getTime()).toISOString(),
      value: register,
    });

    for (let d = 0; d < days; d += 1) {
      const day = new Date(start.getTime() + d * 86400000);
      const wkFactor = isWeekend(day) ? mp.weekendFactor : 1;
      const noise = seededJitter(`${cid}:${mp.meterId}:${d}`, mp.jitter);
      const dailyUse = mp.baseDailyUsage * wkFactor * noise;
      register += Math.round(dailyUse);

      readings.push({
        meterId: mp.meterId,
        meterLabel: mp.label,
        commodity: mp.commodity,
        scheme: mp.scheme,
        identifier: mp.identifier,
        readingKind: mp.readingKind,
        unit: mp.unit,
        timestamp: new Date(day.getTime() + 86399000).toISOString(),
        value: register,
      });
    }
  });

  const batch = {
    batchId,
    source: "SIMULATOR",
    companyId: cid,
    companyName,
    periodStart,
    periodEnd,
    generatedAt: nowIso(),
    meterPointCount: SIMULATED_METER_POINTS.length,
    readingCount: readings.length,
    readings,
  };

  // persist local
  const store = readLocal();
  if (!store.byCompany[cid]) store.byCompany[cid] = [];
  store.byCompany[cid].unshift(batch);
  store.byCompany[cid] = store.byCompany[cid].slice(0, 12); // keep last dozen
  writeLocal(store);

  // best-effort S3 mirror
  try {
    await GenericFileUploader.uploadGeneratedData(batch, {
      bucketName: ELEMENTS_BUCKET,
      fileName: `meter-sim/${cid}/${batchId}.json`,
      labels: ["BlueprintReporting", "MeterSim", cid],
      batchType: 2,
    });
  } catch {
    /* offline / permissions not wired — local copy is enough */
  }

  return batch;
}

// ---------------------------------------------------------------------------
// read-back + roll-up
// ---------------------------------------------------------------------------

export function listBatches(companyId) {
  const cid = companyId != null ? String(companyId) : "unassigned";
  return readLocal().byCompany[cid] || [];
}

export function latestBatch(companyId) {
  return listBatches(companyId)[0] || null;
}

export function clearBatches(companyId) {
  const cid = companyId != null ? String(companyId) : "unassigned";
  const store = readLocal();
  delete store.byCompany[cid];
  writeLocal(store);
}

const UNIT_TO_CANONICAL = {
  Wh: { unit: "kWh", factor: 0.001 },
  kWh: { unit: "kWh", factor: 1 },
  MWh: { unit: "kWh", factor: 1000 },
  L: { unit: "L", factor: 1 },
  "m³": { unit: "L", factor: 1000 },
  m3: { unit: "L", factor: 1000 },
};

/**
 * Collapse every stored batch that overlaps the period into per-field totals.
 * REGISTER  -> (max − min) per meter, unit-converted, then Σ across meters.
 * INTERVAL  -> Σ of values.
 *
 * @returns {{ values: Object<string,number>, meta: Object }}
 *   values keyed by fieldPath (e.g. "energy.electricity_total_kwh")
 */
export function rollupForPeriod(companyId, periodStart, periodEnd) {
  const from = periodStart ? new Date(periodStart).getTime() : -Infinity;
  const to = periodEnd ? new Date(periodEnd).getTime() + 86399000 : Infinity;

  const batches = listBatches(companyId);
  // meterId -> { fieldPath, unit, values: [] }
  const perMeter = new Map();
  const pointByMeter = new Map(SIMULATED_METER_POINTS.map((p) => [p.meterId, p]));
  let usedBatchIds = new Set();

  batches.forEach((b) => {
    (b.readings || []).forEach((r) => {
      const t = new Date(r.timestamp).getTime();
      if (t < from || t > to) return;
      usedBatchIds.add(b.batchId);
      if (!perMeter.has(r.meterId)) {
        perMeter.set(r.meterId, {
          fieldPath: pointByMeter.get(r.meterId)?.fieldPath || null,
          readingKind: r.readingKind,
          unit: r.unit,
          values: [],
        });
      }
      perMeter.get(r.meterId).values.push(Number(r.value) || 0);
    });
  });

  const values = {};
  const contributions = [];

  perMeter.forEach((m, meterId) => {
    if (!m.fieldPath || m.values.length === 0) return;
    let raw;
    if (m.readingKind === "REGISTER") {
      raw = Math.max(...m.values) - Math.min(...m.values);
    } else {
      raw = m.values.reduce((s, v) => s + v, 0);
    }
    const conv = UNIT_TO_CANONICAL[m.unit] || { factor: 1 };
    const amount = raw * conv.factor;
    values[m.fieldPath] = Math.round((values[m.fieldPath] || 0) + amount);
    contributions.push({ meterId, fieldPath: m.fieldPath, amount: Math.round(amount), unit: conv.unit || m.unit });
  });

  return {
    values,
    meta: {
      companyId: companyId != null ? String(companyId) : "unassigned",
      periodStart,
      periodEnd,
      batchesUsed: usedBatchIds.size,
      metersUsed: perMeter.size,
      fieldsFilled: Object.keys(values).length,
      contributions,
    },
  };
}
